import React from 'react';
import ActionButton from '../../components/ui/ActionButton';
import DataTable, { Column } from '../../components/ui/DataTable';
import {
  CoaAccount,
  MappingConfig,
  TYPE_COLORS,
  selectStyle,
  labelStyle,
} from './types';

interface CoAPanelProps {
  entities: any[];
  coaEntity: string;
  setCoaEntity: (v: string) => void;
  coaFile: File | null;
  setCoaFile: (f: File | null) => void;
  coaMappingId: number | null;
  setCoaMappingId: (v: number | null) => void;
  coaReplaceExisting: boolean;
  setCoaReplaceExisting: (v: boolean) => void;
  coaUploading: boolean;
  coaResult: any;
  coaDragOver: boolean;
  setCoaDragOver: (v: boolean) => void;
  coaInputRef: React.RefObject<HTMLInputElement | null>;
  mappings: MappingConfig[];
  accounts: CoaAccount[];
  accountsLoading: boolean;
  editingCode: string | null;
  setEditingCode: (v: string | null) => void;
  editType: string;
  setEditType: (v: string) => void;
  handleCoaUpload: () => void;
  handleAccountTypeUpdate: (accountCode: string, newType: string) => void;
  handleDrag: (e: React.DragEvent, setOver: (v: boolean) => void, over: boolean) => void;
  handleDrop: (e: React.DragEvent, setFile: (f: File | null) => void, setOver: (v: boolean) => void) => void;
}

export default function CoAPanel({
  entities,
  coaEntity,
  setCoaEntity,
  coaFile,
  setCoaFile,
  coaMappingId,
  setCoaMappingId,
  coaReplaceExisting,
  setCoaReplaceExisting,
  coaUploading,
  coaResult,
  coaDragOver,
  setCoaDragOver,
  coaInputRef,
  mappings,
  accounts,
  accountsLoading,
  editingCode,
  setEditingCode,
  editType,
  setEditType,
  handleCoaUpload,
  handleAccountTypeUpdate,
  handleDrag,
  handleDrop,
}: CoAPanelProps) {

  const accountColumns: Column[] = [
    { key: 'account_code', label: 'Code', render: (v: any) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 600 }}>{v}</span> },
    { key: 'account_name', label: 'Name', render: (v: any) => <span style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }}>{v}</span> },
    { key: 'account_type', label: 'Type', render: (v: any, row: any) => {
      const isInferred = v === 'INFERRED' || v === 'UNKNOWN';
      const isEditing = editingCode === row.account_code;
      if (isEditing) {
        return (
          <select
            value={editType}
            onChange={e => setEditType(e.target.value)}
            onBlur={() => {
              if (editType && editType !== v) {
                handleAccountTypeUpdate(row.account_code, editType);
              } else {
                setEditingCode(null);
              }
            }}
            autoFocus
            style={{ ...selectStyle, fontSize: 10, padding: '2px 4px' }}
          >
            {['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        );
      }
      return (
        <span
          onClick={() => { setEditingCode(row.account_code); setEditType(v); }}
          style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
            cursor: 'pointer',
            color: isInferred ? '#f59e0b' : TYPE_COLORS[v] || '#94a3b8',
            background: isInferred ? '#fffbeb' : 'rgba(148,163,184,0.08)',
            border: isInferred ? '1px dashed rgba(245,158,11,0.4)' : 'none',
          }}
        >
          {v}
        </span>
      );
    }},
    { key: 'statement', label: 'Statement', render: (v: any) => <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{v}</span> },
    { key: 'normal_balance', label: 'Normal Balance', render: (v: any) => <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{v}</span> },
    { key: 'source', label: 'Source', render: (v: any) => (
      <span style={{
        padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600,
        color: v === 'SEEDED' ? 'var(--navy-600)' : v === 'UPLOADED' ? 'var(--positive)' : '#f59e0b',
        background: v === 'SEEDED' ? 'rgba(96,165,250,0.1)' : v === 'UPLOADED' ? '#dcfce7' : 'rgba(245,158,11,0.1)',
      }}>
        {v}
      </span>
    )},
    { key: 'is_active', label: 'Active', render: (v: any) => <span style={{ fontSize: 11, color: v ? 'var(--positive)' : '#ef4444' }}>{v ? 'Yes' : 'No'}</span> },
  ];

  return (
    <>
      {/* Upload form */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-default)',
        borderRadius: 8, padding: 20, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Entity</label>
            <select value={coaEntity} onChange={e => setCoaEntity(e.target.value)} style={selectStyle}>
              <option value="">Select entity...</option>
              {entities.map((ent: any) => (
                <option key={ent.entity_code} value={ent.entity_code}>
                  {ent.entity_code} - {ent.entity_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Mapping (CoA columns)</label>
            <select value={coaMappingId ?? ''} onChange={e => setCoaMappingId(e.target.value ? parseInt(e.target.value) : null)} style={selectStyle}>
              <option value="">Auto-detect (default columns)</option>
              {mappings.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer', paddingBottom: 4 }}>
            <input
              type="checkbox"
              checked={coaReplaceExisting}
              onChange={e => setCoaReplaceExisting(e.target.checked)}
            />
            Replace existing CoA
          </label>
        </div>

        {/* Drop zone */}
        <div
          onDragEnter={e => handleDrag(e, setCoaDragOver, coaDragOver)}
          onDragOver={e => handleDrag(e, setCoaDragOver, coaDragOver)}
          onDragLeave={e => handleDrag(e, setCoaDragOver, coaDragOver)}
          onDrop={e => handleDrop(e, setCoaFile, setCoaDragOver)}
          onClick={() => coaInputRef.current?.click()}
          style={{
            border: `2px dashed ${coaDragOver ? 'var(--accent)' : 'var(--border-subtle)'}`,
            borderRadius: 8, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
            background: coaDragOver ? 'var(--accent-subtle)' : 'transparent',
            transition: 'all 0.15s ease',
            marginBottom: 16,
          }}
        >
          <input
            ref={coaInputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={e => setCoaFile(e.target.files?.[0] || null)}
            style={{ display: 'none' }}
          />
          {coaFile ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{coaFile.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                {(coaFile.size / 1024).toFixed(1)} KB
                <span style={{ marginLeft: 8, cursor: 'pointer', color: '#ef4444' }}
                  onClick={e => { e.stopPropagation(); setCoaFile(null); }}>
                  Remove
                </span>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Drag & drop CoA file (CSV or Excel)
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Expected columns: account_code, account_name, account_type
              </div>
            </div>
          )}
        </div>

        <ActionButton
          label={coaUploading ? 'Uploading...' : 'Upload CoA'}
          onClick={() => handleCoaUpload()}
          variant="primary"
          disabled={!coaFile || !coaEntity || coaUploading}
        />
      </div>

      {/* CoA upload result */}
      {coaResult && (
        <div style={{
          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
          borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--positive)', marginBottom: 8 }}>Upload Result</div>
          <div style={{ display: 'flex', gap: 20 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Created: <strong style={{ color: 'var(--text-primary)' }}>{coaResult.accounts_created}</strong></span>
            <span style={{ color: 'var(--text-secondary)' }}>Updated: <strong style={{ color: 'var(--text-primary)' }}>{coaResult.accounts_updated}</strong></span>
            <span style={{ color: 'var(--text-secondary)' }}>Rejected: <strong style={{ color: coaResult.accounts_rejected > 0 ? '#fca5a5' : '#fff' }}>{coaResult.accounts_rejected}</strong></span>
          </div>
          {coaResult.warnings && coaResult.warnings.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {coaResult.warnings.map((w: string, i: number) => (
                <div key={i} style={{ fontSize: 11, color: '#f59e0b' }}>{w}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info banner */}
      <div style={{
        background: 'rgba(59,130,246,0.06)', border: '1px solid var(--accent-subtle)',
        borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 11, color: 'var(--text-secondary)',
      }}>
        Upload a Chart of Accounts before GL data for best results.
        If no CoA is uploaded, accounts will be auto-extracted from GL with inferred types.
        INFERRED accounts are highlighted below for review — click the type badge to correct.
      </div>

      {/* Current accounts table */}
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
        Current Accounts for Entity: {coaEntity || '(select entity)'}
      </div>

      {!coaEntity ? (
        <div style={{
          background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)',
          padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13,
        }}>
          Select an entity to view its chart of accounts.
        </div>
      ) : (
        <>
          {accounts.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8 }}>
              {accounts.length} accounts
              {accounts.filter(a => a.account_type === 'INFERRED').length > 0 && (
                <span style={{ marginLeft: 8, color: '#f59e0b' }}>
                  ({accounts.filter(a => a.account_type === 'INFERRED').length} need classification)
                </span>
              )}
            </div>
          )}
          <DataTable
            columns={accountColumns}
            rows={accounts}
            loading={accountsLoading}
            emptyMessage="No accounts for this entity. Upload a CoA file or upload GL data to auto-extract accounts."
            rowKey="account_code"
          />
        </>
      )}
    </>
  );
}
